<template lang="pug">
.file-input(:style="{ height: height + 'px' }")
  el-button-group.actions
    el-button(
      size="mini"
      type="danger"
      v-tooltip="'Remove'"
      v-if="clearable && value"
      @click.stop="clear")
      i.bx.bx-trash
  label.preview
    .content(v-if="preview")
      template(v-if="previewSource || imgDefault")
        .image(
          :style="{ transform: `rotate(${rotate}deg)` }"
          v-if="!isPdf"
          key="image"
          v-image="previewSource || imgDefault"
          v-tooltip="'Click to change'")
        .image.pdf(v-else)
          i.bx.bx-file-blank.bx-6
          .sub-text No preview available
      .img-default(key="placeholder" v-else)
        i.icon.bx.bxs-cloud-upload.bx-14
        .text(v-if="text") {{ text }}
        .sub-text(v-if="subText") {{ subText }}

    .input
      input(
        type="file"
        ref="file"
        :accept="acceptString"
        :multiple="multiple"
        @change="change")

    .list.pf-ellipsis(v-if="showList")
      help-tag.ml-0.mr-1(tooltip="The maximum file size is 10MB")
      span.message.pf-ellipsis(v-if="files.length")
        span(v-for="i, k in files" :key="k" v-text="i.name")
      span.message.pf-ellipsis(v-else v-text="placeholder")
</template>

<script>
import { FILE_TYPES, MAX_ATTACHMENT_SIZE } from '~/settings'

export default {
  name: 'FileInput',

  props: {
    multiple: {
      type: Boolean,
      default: false
    },

    accept: {
      type: Array,
      default: () => Object.keys(FILE_TYPES)
    },

    maxFileSize: {
      type: Number,
      default: MAX_ATTACHMENT_SIZE
    },

    preview: {
      type: Boolean,
      default: false
    },

    showList: {
      type: Boolean,
      default: false
    },

    clearable: {
      type: Boolean,
      default: false
    },

    placeholder: {
      type: String,
      default: 'Select a file'
    },

    text: {
      type: String,
      default: null
    },

    height: {
      type: Number,
      default: 230
    },

    subText: {
      type: String,
      default: null
    },

    imgDefault: {
      type: String,
      default: null
    },

    value: {
      type: null,
      default: null
    },

    rotate: {
      type: Number,
      default: 0
    }
  },

  data () {
    return {
      files: [],
      previewSrc: null,
      reader: null,
      isPdf: false
    }
  },

  computed: {
    acceptString () {
      const mimes = this.accept
        .map(i => FILE_TYPES[i])
        .filter(i => !!i)
      return Array
        .from(new Set(mimes))
        .join(',')
    },

    tooltipString () {
      const types = Array
        .from(new Set(this.accept))
        .join(', ')
        .toUpperCase()
      return `Accept: ${types}`
    },

    fileExtension () {
      const mimes = this.accept
        .map(i => FILE_TYPES[i])
        .filter(i => !!i)
      return mimes
    },

    previewSource () {
      return this.preview && this.previewSrc
        ? this.previewSrc
        : null
    }
  },

  mounted () {
    if (!this.reader) {
      this.reader = new FileReader()
      this.reader.onload = () => {
        const res = this.reader.result

        this.previewSrc = res.startsWith('data:image') ? encodeURI(res) : null
        this.isPdf = res.startsWith('data:application/pdf')
      }
    }
  },

  methods: {
    /**
     * Using this method for some cases that you want to select the same file again.
     * For example: LP > Order > beforeUploadPOD
     */
    deepClearFileInput () {
      this.$refs.file.value = ''
    },

    clear (e) {
      this.deepClearFileInput()
      this.files = []
      this.previewSrc = null
      this.$emit('input', null)
      this.$emit('clear', e)
    },

    change (e) {
      const files = e.target.files
      let res = this.multiple ? [] : {}
      const invalidFiles = Array.from(files).filter(f => !this.fileExtension.includes(f.type))
      if (invalidFiles.length) {
        this.$workflow.error('Invalid file type')
        e.target.value = ''
        return
      }

      const maxFiles = Array.from(files).filter(f => f.size > this.maxFileSize)
      if (maxFiles.length) {
        this.$workflow.error('Maximum file size exceeded.')
        e.target.value = ''
        return
      }

      this.files = [...files]

      if (!this.multiple) {
        res = files[0]
      } else {
        for (let i = 0; i < files.length; i++) {
          res.push(files[i])
        }
      }

      if (this.preview && !this.multiple && files.length) {
        this.reader.readAsDataURL(files[0])
      }
      this.$emit('input', res)
      this.$emit('change', e, res)
    }
  }
}
</script>
<style lang="scss" scoped>
.file-input {
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  position: relative;

  .actions {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
  }

  .preview {
    position: relative;
    display: block;
    background-color: #f4f6f8;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    width: 100%;
    height: 100%;

    .img-default {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      transform: translateY(-50%);

      .icon {
        color: $--color-text-secondary;
      }

      .text, .sub-text {
        color: $--color-text-secondary;
        font-size: .9em;
      }
    }

    .image {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;

      &.pdf {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-size: 1rem;

        .sub-text {
          font-size: .8em;
          color: $--color-text-secondary;
        }
      }
    }
    .input {
      text-align: center;
      .icon {
        color: $--color-primary;
      }
    }
    .list {
      padding: 5px;
      .message {
        font-size: 12px;
        line-height: 1.25em;
        color: $--color-text-secondary;
      }
    }
  }
  input {
    display: none;
    opacity: 0;
    display: none;
  }
}
</style>
